import React from 'react'
import Modal from "react-modal";
import { useSurface, SurfaceProvider } from "../lib/surface-context";
import { surfaceClasses } from "../lib/surface-classes";

/**
 * The dialog shell — every modal in the portal is this box.
 *
 * `className` / `overlayClassName` are what make react-modal drop its own base
 * classes AND its default inline styles, so the panel is described in
 * theme.css (the DIALOGS block) instead of in a per-file `customStyles`
 * object. Anything still passing react-modal's `style` prop fights that block.
 *
 * The shape is fixed: a bold title, muted supporting copy, then the actions
 * flush right with the safe one first. Three slots keep it that way —
 *
 *   heading      the title. Optional: the image viewer in UserEdit passes
 *                none, and then the close control floats over the body.
 *   description  the sentence under the title. Muted, never a label.
 *   children     the body — a form, a record, an image.
 *   footer       the actions. Use <button className="app-dialog-btn
 *                app-dialog-btn-ghost"> for cancel and
 *                `app-dialog-btn-primary` / `-danger` for the write.
 *
 * `size` picks the width: "sm" is the 420px confirm, "lg" the 1080px box a
 * wide table needs, and the default is the 560px content dialog.
 * `showClose={false}` drops the ✕ for a dialog whose only exits are its own
 * two buttons (see ConfirmDialog).
 *
 * Elevation: the panel sits four surface steps above wherever it was opened
 * (lib/surface-context) and re-provides that level, so a date picker or menu
 * opened inside it lands further up the ladder instead of melting into it.
 */
const CustomModal = ({
    isShow,
    onHide = () => { },
    heading = "",
    description = "",
    footer = null,
    size = "",
    showClose = true,
    children,
}) => {

    const closeButton = (extraClass = "") => (
        <button
            type="button"
            className={`app-dialog-close ${extraClass}`.trim()}
            aria-label="Close"
            onClick={onHide}
        >
            <i className="fa fa-times" aria-hidden="true"></i>
        </button>
    );

    const hasBody = description || children;
    const level = Math.min(useSurface() + 4, 8);

    return (
        <Modal
            isOpen={isShow}
            preventScroll={true}
            ariaHideApp={false}
            contentLabel={heading || "Dialog"}
            className={`app-dialog${size ? ` app-dialog-${size}` : ""} ${surfaceClasses(level)}`}
            overlayClassName="app-dialog-overlay"
            onRequestClose={onHide}
        >
          <SurfaceProvider value={level}>
            {heading ? (
                <div className="app-dialog-head">
                    <h3>{heading}</h3>
                    {showClose ? closeButton() : null}
                </div>
            ) : (
                showClose ? closeButton("app-dialog-close-float") : null
            )}
            {hasBody ? (
                <div className="app-dialog-body">
                    {description ? <p className="app-dialog-lede">{description}</p> : null}
                    {children}
                </div>
            ) : null}
            {footer ? <div className="app-dialog-foot">{footer}</div> : null}
          </SurfaceProvider>
        </Modal>
    )
}

export default CustomModal
